import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Keyboard shortcuts (Linear-style). Bindings are combos like `c`, `shift+c`, `mod+z`,
 * `shift+mod+z`, `[`, `Backspace`, `Escape`, `ArrowDown`, or two-key sequences like `g a`
 * (pressed within a second). `mod` is ⌘ on macOS and Ctrl elsewhere. Shortcuts are ignored while
 * typing in a field and while a dialog or menu is open. They are documented in `./shortcuts.ts`.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

/** Normalises a keyboard event to a combo string. Shift is implied by symbols such as `?`. */
export function comboOf(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>,
): string {
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  const isLetter = /^[a-z]$/.test(key);
  const parts: string[] = [];
  if (event.shiftKey && (isLetter || event.key.length > 1)) parts.push('shift');
  if (event.metaKey || event.ctrlKey) parts.push('mod');
  if (event.altKey) parts.push('alt');
  parts.push(key);
  return parts.join('+');
}

/** A dialog, menu or listbox has the keyboard (non-modal overlays such as the tour don't count). */
function overlayOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"]:not([data-nonmodal]), [role="menu"], [role="listbox"]',
    ) !== null
  );
}

/** Time allowed between the keys of a sequence such as `g a`. */
export const SEQUENCE_MS = 1000;

export function useHotkeys(bindings: Record<string, (() => void) | undefined>): void {
  const ref = useRef(bindings);
  const pending = useRef<{ key: string; at: number } | null>(null);
  useLayoutEffect(() => {
    ref.current = bindings;
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.isComposing) return;
      if (isTypingTarget(event.target) || overlayOpen()) return;
      const combo = comboOf(event);
      // The second key of a sequence wins over a single-key binding.
      const previous = pending.current;
      pending.current = null;
      if (previous && Date.now() - previous.at <= SEQUENCE_MS) {
        const sequence = ref.current[`${previous.key} ${combo}`];
        if (sequence) {
          event.preventDefault();
          sequence();
          return;
        }
      }
      const startsSequence = Object.keys(ref.current).some(
        (k) => k.startsWith(`${combo} `) && ref.current[k] !== undefined,
      );
      if (startsSequence) pending.current = { key: combo, at: Date.now() };
      const handler = ref.current[combo];
      if (!handler) return;
      if (event.repeat && !event.key.startsWith('Arrow') && event.key !== 'j' && event.key !== 'k')
        return;
      event.preventDefault();
      handler();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}
